import React, { useState } from 'react';
import { User, Mail, MapPin, ArrowRight } from 'lucide-react';
import Logo from '../Logo';
import toast from 'react-hot-toast';

interface PersonalDetailsFormProps {
  phoneNumber: string;
  onComplete: (details: PersonalDetails) => void;
  loading?: boolean;
}

export interface PersonalDetails {
  name: string;
  email?: string;
  city: string;
  pincode: string;
}

const PersonalDetailsForm: React.FC<PersonalDetailsFormProps> = ({
  phoneNumber,
  onComplete,
  loading = false
}) => {
  const [formData, setFormData] = useState<PersonalDetails>({
    name: '',
    email: '',
    city: '',
    pincode: ''
  });

  const [errors, setErrors] = useState<Partial<PersonalDetails>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<PersonalDetails> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onComplete(formData);
    } else {
      toast.error('Please fix the errors and try again');
    }
  };

  const handleInputChange = (field: keyof PersonalDetails, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-primary-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {}
        <div className="text-center mb-8">
          {}
          <div className="flex justify-center mb-4">
            <Logo size="xl" showText={false} />
          </div>
          
          {}
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Shetkari Agro Fresh</h1>
          <p className="text-sm text-gray-600 mb-4">Farm Fresh • Home Delivered</p>
          
          {}
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Welcome!</h2>
          <p className="text-gray-600">Let's set up your profile to get started</p>
          <div className="mt-4 bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-primary-200">
            <p className="text-sm text-gray-700">
              📱 Phone: <span className="font-semibold text-primary-600">{phoneNumber}</span>
            </p>
          </div>
        </div>

        {}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter your full name"
                  className={`input-field pl-10 ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  required
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email address"
                  className={`input-field pl-10 ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Enter your city"
                  className={`input-field pl-10 ${errors.city ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  required
                />
              </div>
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city}</p>
              )}
            </div>

            {}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pincode *
              </label>
              <input
                type="text"
                value={formData.pincode}
                onChange={(e) => handleInputChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit pincode"
                className={`input-field ${errors.pincode ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                maxLength={6}
                required
              />
              {errors.pincode && (
                <p className="mt-1 text-sm text-red-600">{errors.pincode}</p>
              )}
            </div>

            {}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  Complete Profile
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>

        {}
        <div className="mt-6 bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-primary-200">
          <h3 className="font-semibold text-gray-800 mb-3 text-center">🌾 Why Shetkari Agro Fresh?</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <div className="flex items-center">
              <span className="text-green-500 mr-2">🚚</span>
              <span>Fresh delivery from farm to your doorstep</span>
            </div>
            <div className="flex items-center">
              <span className="text-green-500 mr-2">🌱</span>
              <span>100% organic and pesticide-free products</span>
            </div>
            <div className="flex items-center">
              <span className="text-green-500 mr-2">⏰</span>
              <span>Early morning delivery (6:00 AM - 9:00 AM)</span>
            </div>
            <div className="flex items-center">
              <span className="text-green-500 mr-2">💰</span>
              <span>Best prices directly from farmers</span>
            </div>
          </div>
        </div>

        {}
        <p className="text-center text-xs text-gray-500 mt-6">
          By completing your profile, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default PersonalDetailsForm;
